import i18n from '../i18n';
import { pb } from '../lib/pb';
import { errorCode, errorMessage } from '../lib/errors';
import type { Status } from '../lib/types';
import { toast } from '../store/store';
import { getData, setData } from './dataStore';
import { same } from './values';
import type { DataRecord, DataSheetRecord, Values } from './types';

// Writes to game-data records. The server (pb_hooks/lib/data-edit.js) validates values, locks keys and
// runs the status workflow; the client only sends what the designer changed plus the rev it started from.

function put(rec: DataRecord) {
  setData({ records: { ...getData().records, [rec.id]: rec } });
}

export type SaveResult = { ok: true; record: DataRecord } | { ok: false; conflict: boolean };

/** Saves values (and the note to Niklas) against the rev the edit started from. */
export async function saveRecord(
  id: string,
  baseRev: number,
  patch: { values?: Values; needsNiklas?: string; status?: Status },
): Promise<SaveResult> {
  try {
    const rec = await pb.collection('data_records').update<DataRecord>(id, { rev: baseRev, ...patch });
    put(rec);
    return { ok: true, record: rec };
  } catch (err) {
    if (errorCode(err) === 'stale_revision') return { ok: false, conflict: true };
    toast(errorMessage(err), 'error');
    return { ok: false, conflict: false };
  }
}

export async function setRecordStatus(r: DataRecord, status: Status) {
  const res = await saveRecord(r.id, r.rev, { status });
  if (!res.ok && res.conflict) toast(i18n.t('errors.stale_revision'), 'error');
}

/** take_unity = reject the change (back to Unity's values); keep_design = the design wins a conflict. */
export async function resolveRecord(id: string, action: 'take_unity' | 'keep_design') {
  try {
    await pb.send(`/api/gamedata/records/${id}/resolve`, { method: 'POST', body: { action } });
    put(await pb.collection('data_records').getOne<DataRecord>(id));
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}

export async function reloadRecord(id: string): Promise<DataRecord | null> {
  try {
    const rec = await pb.collection('data_records').getOne<DataRecord>(id);
    put(rec);
    return rec;
  } catch (err) {
    toast(errorMessage(err), 'error');
    return null;
  }
}

// Sheet view edits come quickly one after another (type, Tab, type): one queue per record, so each save
// starts from the rev the previous one returned, and each edit is applied to the values as they are then.
const queues = new Map<string, Promise<boolean>>();

function enqueue(key: string, run: () => Promise<boolean>): Promise<boolean> {
  const p = (queues.get(key) ?? Promise.resolve(true)).then(run, run);
  queues.set(key, p);
  void p.finally(() => {
    if (queues.get(key) === p) queues.delete(key);
  });
  return p;
}

/**
 * Saves one cell-sized edit: `edit` gets the record's latest values. When someone else saved in between
 * (409), the record is reloaded and the edit applied once more on top of theirs.
 */
export function queueRecordEdit(id: string, edit: (values: Values) => Values): Promise<boolean> {
  return enqueue(id, async () => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const r = getData().records[id];
      if (!r) return false;
      const values = r.values ?? {};
      const next = edit(values);
      if (same(next, values)) return true;
      const res = await saveRecord(id, r.rev, { values: next });
      if (res.ok) return true;
      if (!res.conflict || !(await reloadRecord(id))) return false;
    }
    toast(i18n.t('errors.stale_revision'), 'error');
    return false;
  });
}

function putSheet(row: DataSheetRecord) {
  setData({ sheet: { ...getData().sheet, [row.record]: row } });
}

/** Sets one of the sheet's own columns (difficulty, notes) of a record; '' clears it. Web-only. */
export function saveSheetValue(recordId: string, column: string, value: string): Promise<boolean> {
  return enqueue('sheet:' + recordId, async () => {
    const values = { [column]: value };
    const existing = getData().sheet[recordId];
    try {
      putSheet(
        existing
          ? await pb.collection('data_sheet').update<DataSheetRecord>(existing.id, { values })
          : await pb.collection('data_sheet').create<DataSheetRecord>({ record: recordId, values }),
      );
      return true;
    } catch (err) {
      if (!existing) {
        // Someone else started this record's row a moment ago: write into theirs.
        try {
          const theirs = await pb
            .collection('data_sheet')
            .getFirstListItem<DataSheetRecord>(pb.filter('record = {:r}', { r: recordId }));
          putSheet(await pb.collection('data_sheet').update<DataSheetRecord>(theirs.id, { values }));
          return true;
        } catch {
          /* report the first error */
        }
      }
      toast(errorMessage(err), 'error');
      return false;
    }
  });
}

/** Only new assets (not in Unity yet) can be deleted; the server checks nothing still points at them. */
export async function deleteRecord(id: string) {
  try {
    await pb.collection('data_records').delete(id);
    const records = { ...getData().records };
    delete records[id];
    setData({ records, selectedId: getData().selectedId === id ? null : getData().selectedId });
  } catch (err) {
    toast(errorMessage(err), 'error');
  }
}
